/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {useContext, useState, useEffect, useMemo, useCallback} from 'react';
import appStore from 'client/AppStore';
import {useTracking} from 'react-tracking';

import {
  Button,
  Icon,
  TextField,
  Text
  // addToast
} from '@albert-io/atomic';

import {ContentDiscoveryContext} from '../../ContentDiscoveryProvider.react';
import SettingsModal from '../SettingsModal/SettingsModal.react';

import ConfirmLeaveWrapper from './ConfirmLeaveHandler.react';
import BetaFeedBackLink from '../BetaFeedBackLink.react';
import {FolderOptionsDropdown, FolderModal} from './ConvertToFolder';
import './assignment-navigation.scss';

interface AssignmentNavigationProps {
  route: string | {path: string};
  onReset: () => void;
  showAssignmentSharing: boolean;
}

const AssignmentNavigation = ({
  route,
  onReset,
  showAssignmentSharing
}: AssignmentNavigationProps) => {
  const {
    draftAssignment,
    currentPage,
    setCurrentPage,
    draftHasSavedDuringSession,
    draftHasPendingChanges,
    updateDraftName,
    autoSave,
    manualSave,
    hideSaving,
    selectedQuestionSets
  } = useContext(ContentDiscoveryContext);
  const [draftName, setDraftName] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [draftNameError, setDraftNameError] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolder, setNewFolder] = useState(null);

  const {query} = appStore.routerProps.location;

  const loadedDraftFromURL = Object.prototype.hasOwnProperty.call(query, 'draftId');

  const {trackEvent} = useTracking({
    component: 'assignment header'
  });

  useEffect(() => {
    if (draftAssignment) {
      setDraftName(draftAssignment.getName());
    }
  }, [draftAssignment]);

  const unsavedNameChanges = useMemo(() => {
    if (!draftAssignment) {
      return draftName.length > 0;
    }
    return draftHasPendingChanges;
  }, [draftAssignment, draftName, draftHasPendingChanges]);

  const handleManualDraftSave = () => {
    trackEvent({event: 'click', content: 'Save a draft', type: 'button'});
    if (draftName && draftName.length > 0) {
      setDraftNameError(false);
      manualSave(draftName);
    } else {
      setDraftNameError(true);
    }
  };

  let saveText;
  if (draftHasPendingChanges) {
    saveText = 'Saving changes...';
  } else if (draftHasSavedDuringSession) {
    saveText = 'All changes saved';
  }

  const renderSaveButton = useCallback(() => {
    if (hideSaving) {
      return <BetaFeedBackLink />;
    }
    return (
      <>
        {autoSave || loadedDraftFromURL ? (
          <Text className='u-mar-r_2'>{saveText}</Text>
        ) : (
          <Button
            onClick={handleManualDraftSave}
            className='u-mar-r_2'
            color='secondary'
            variant='outlined'
          >
            Save A Draft
          </Button>
        )}
      </>
    );
  }, [autoSave, loadedDraftFromURL, hideSaving]);

  const renderOptionsDropdown = useCallback(() => {
    if (hideSaving) {
      return <FolderOptionsDropdown onClick={() => setShowFolderModal(true)} />;
    }
    return null;
  }, []);

  return (
    <header className='assignment-navigation-wrapper u-display_flex u-justify-content_space-between u-align-items_center'>
      {currentPage === 'add' ? (
        <Button className='u-height_fit-content' onClick={() => setCurrentPage('review')}>
          <Icon className='u-mar-r_1' icon='arrow-left' /> Done adding questions
        </Button>
      ) : (
        <TextField
          data-test-id='AB__assignment-name-input'
          size='l'
          className='u-max-width_240'
          value={draftName}
          placeholder='Unnamed assignment'
          onBlur={() => {
            if (autoSave) updateDraftName(draftName);
          }}
          onChange={(e) => {
            if (e.target.value) {
              setDraftNameError(false);
            }
            trackEvent({
              content: e.target.value,
              event: 'click',
              type: 'textbox'
            });
            setDraftName((e.target as HTMLInputElement).value);
          }}
          error={draftNameError}
          message={draftNameError && 'Provide an assignment name'}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              if (autoSave) updateDraftName((e.target as HTMLInputElement).value);
            }
          }}
        />
      )}
      <div className='u-display_flex u-align-items_center'>
        {renderSaveButton()}
        {renderOptionsDropdown()}
        {currentPage !== 'add' && (
          <Button
            data-test-id='AB__assign-btn'
            onClick={() => {
              setShowSettingsModal(true);
              trackEvent({event: 'click', content: 'assign', type: 'button'});
            }}
          >
            Assign
          </Button>
        )}
      </div>
      {showSettingsModal && (
        <SettingsModal
          handleClose={() => setShowSettingsModal(false)}
          setDraftName={setDraftName}
          draftName={draftName}
          setDraftNameError={setDraftNameError}
          draftNameError={draftNameError}
        />
      )}
      {showFolderModal && (
        <FolderModal
          handleClose={() => setShowFolderModal(false)}
          onFolderCreation={(folder) => setNewFolder(folder)}
          questionsList={selectedQuestionSets}
          assignmentName={draftName}
          onDismiss={() => setNewFolder(null)}
        />
      )}
      <ConfirmLeaveWrapper
        draftName={draftName}
        unsavedNameChanges={unsavedNameChanges}
        openFolderModal={() => setShowFolderModal(true)}
        newFolder={newFolder}
        route={route}
        onReset={onReset}
        showAssignmentSharing={showAssignmentSharing}
      />
    </header>
  );
};

export default AssignmentNavigation;
